/**
 * When a photo is shrunk on the phone before it is sent, and to what size (B82).
 *
 * The live server, 512 MB on one CPU, stopped in the middle of a photo upload: every full-size phone photo is decoded
 * there several times. Since B82 every photo longer than the shrink box (APP_CONSTANTS.PHOTO MAX_WIDTH and
 * MAX_HEIGHT, 2048 pixels) is drawn inside it before sending, not only one over the server's 5 MB or 6,000 pixels.
 * A 12-megapixel phone photo goes from about 3 to 5 MB to well under 1 MB, and the server's work and memory drop
 * about four times. A photo already inside the box and light enough is sent as it is, so it is not compressed twice;
 * one the browser cannot measure is left to the server to judge, unless it is too heavy.
 *
 * Plain TypeScript with no imports, so node --test can run it (photoShrink.b82.test.mjs). Image engineering done on
 * the device (rule 1); the box and the quality are kept in code as K4 (config.ts APP_CONSTANTS.PHOTO).
 *
 * Who uses it: PhotoProcessor (through preparePhoto, every photo door).
 *
 * @module photoShrink
 */

/** What decides whether a photo is shrunk. */
export interface ShrinkInputs {
  /** Its longest side in pixels, or null when the browser could not measure it. */
  edge: number | null;
  /** Its size in bytes. */
  bytes: number;
  /** The largest file sent as it is, in bytes (Infinity before the app has heard the server's limit). */
  maxBytes: number;
  /** The longest side sent as it is, in pixels: the shrink box's. */
  maxEdge: number;
}

/**
 * Whether a photo is shrunk before it is sent: when it is longer than the box on a side, or heavier than the server
 * takes.
 *
 * @param inputs - Its longest side and size, and the limits.
 * @returns True to draw it smaller.
 */
export function needsShrink({ edge, bytes, maxBytes, maxEdge }: ShrinkInputs): boolean {
  if (bytes > maxBytes) return true;
  return edge !== null && edge > maxEdge;
}

/**
 * The size a photo is drawn at: inside maxWidth by maxHeight, its shape kept, never enlarged.
 *
 * @param width - Its width in pixels.
 * @param height - Its height in pixels.
 * @param maxWidth - The box's width.
 * @param maxHeight - The box's height.
 * @returns The width and height to draw it at.
 */
export function shrinkSize(width: number, height: number, maxWidth: number, maxHeight: number): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) return { width, height };
  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return { width: Math.floor(width * ratio), height: Math.floor(height * ratio) };
}
